import { useState, useEffect, useContext, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import PulseLoader from "react-spinners/PulseLoader";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { Card, Badge, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import CardMedia from "@mui/material/CardMedia";
import TableRowsIcon from "@mui/icons-material/TableRows";
import GridViewIcon from "@mui/icons-material/GridView";
import IconButton from "@mui/material/IconButton";
import ToggleButton from "@mui/material/ToggleButton";
import CloseIcon from "@mui/icons-material/Close";

import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import EditIcon from "@mui/icons-material/Edit";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialIcon from "@mui/material/SpeedDialIcon";
import SpeedDialAction from "@mui/material/SpeedDialAction";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiAvatar from "components/SuiAvatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import Avatar from "@mui/material/Avatar";

import SuiBadge from "components/SuiBadge";
import ConversationList from "components/UpStyled/Conversations";
import ReadMore from "components/UpStyled/ReadMore";

import toast from "react-hot-toast";
import moment from "moment";
import { capitalCase } from "capital-case";
import { useForm, Controller } from "react-hook-form";

import api from "services/api";
import ChatRoom from "pages/messages/chatroom";
import { AccountContext } from "context/account";
import { StoreContext } from "context/store";
import CategoryContext from "context/category-context";
import ProjectStyles from "./components/ProjectStyles";
import ProjectInfo from "./components/ProjectInfo";
import ContactAvatar from "components/UpStyled/ContactAvatar";
import { MAX_SELECTED_COUNT } from "utils/params";
import ProjectStatusTag from "components/UpStyled/ProjectStatusTag";
import { filterParser } from "utils/helper";
import Prompt from "components/UpStyled/Prompt";
import DraggableDialog from "components/UpStyled/DraggableDialog";

import Error404 from "pages/error/404";
import { Divider } from "antd";
import ChatIcon from "components/Icon/ChatIcon";

import { backendServer } from "services/request";
import socketIOClient from "socket.io-client";
import { isSafari } from "utils/helper";
import Draggable, { DraggableCore } from "react-draggable";
import { nanoid } from "nanoid";

const ProjectDetail = ({ project, pageLoading = false }) => {
  const [data, setData] = useState({});
  const [chatrooms, setChatRooms] = useState(null);
  const [selectedChatroomId, setSelectedChatroomId] = useState(null);
  const [chatroomKey, setChatroomKey] = useState(nanoid());
  const [provider, setProvider] = useState({});
  const [tabIndex, setTabIndex] = useState(0);
  const [categoryLabel, setCategoryLabel] = useState(""); //Using Category Name if the category ID was found at category tree
  const [unReadCount, setUnReadCount] = useState(0);
  const [privateVisible, setPrivateVisible] = useState(false);
  const [isPublic, setIsPublic] = useState(project.visibility == "private" ? false : true);
  const [chatVisible, setChatVisible] = useState(false);
  const [shake, setShake] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [open, setOpen] = useState(false);
  const [messageDialPosition, setMessageDialPosition] = useState({
    left: "0%",
    top: "0%",
  });
  const [dragStartPosition, setDragStartPosition] = useState({ left: 0, top: 0 });
  const [updateStatusDailogInfo, setUpdateStatusDailogInfo] = useState({ show: false });
  const [dragging, setDragging] = useState(false);

  const NEW_CHAT_MESSAGE_EVENT = "newChatMessage"; // Name of the event
  const account = useContext(AccountContext);
  const { store } = useContext(StoreContext);

  const projectInfoRef = useRef(null);
  const dialButtonRef = useRef(null);

  useEffect(() => {
    if (dialButtonRef?.current) {
      setMessageDialPosition({
        left: `calc(95% - ${dialButtonRef?.current.offsetWidth}px)`,
        top: `calc(0% + ${dialButtonRef?.current.offsetHeight}px)`,
      });
    }
  }, [dialButtonRef?.current, chatrooms]);

  useEffect(() => {
    setData(project);
    if (project.provider_id) {
      api.account
        .get(project.provider_id)
        .then(({ data }) => {
          const stores = data?.user?.stores || [];
          if (stores.length > 0) {
            setProvider(stores[0]);
          }
        })
        .catch((err) => console.log(err));
    }
    if (project.owner_id) {
      if (project.provider_id) {
        try {
          api.chatroom
            .addChatRoom({ userids: [project.provider_id], regardingid: project.id })
            .then(({ data }) => {
              setChatRooms([data]);
              return data;
            });
        } catch (err) {
          toast.error(`addChatRoom failed!`);
          console.log(err);
        }
      }
      refreshChatRooms(project);
    }
  }, [project.provider_id, project, selectedChatroomId]);

  useEffect(() => {
    if (project.id) {
      let roomId = account.id;
      const chatSocket = socketIOClient(backendServer, {
        query: { roomId },
      });
      chatSocket.on(NEW_CHAT_MESSAGE_EVENT, (message) => {
        if (message.projectid == project.id) {
          refreshChatRooms(project);
        }
      });
      return () => {
        chatSocket?.disconnect();
      };
    }
  }, [project.id]);

  const refreshChatRooms = async (project) => {
    if (project?.id) {
      try {
        api.chatroom.listChatRoom("", "", project.id).then(({ data }) => {
          var chats = data.chats;
          if (project.provider_id && project.provider_id !== "")
            chats = chats.filter((c) => c.contactid === project.provider_id);
          setChatRooms(chats);
          var cnt = 0;
          for (const room of data.chats) {
            if (project.provider_id && room?.contactid == project.provider_id) {
              if (room.ts > room.viewts) cnt += 1;
            } else if (!project.provider_id) {
              if (room.ts > room.viewts) cnt += 1;
            }
          }
          if (cnt > 0) {
            setShake(true);
            setTimeout(() => setShake(false), 1000);
          }
          setUnReadCount(cnt);
          return data;
        });
      } catch (err) {
        toast.error(`listChatRoom failed!`);
        console.log(err);
      }
    }
  };

  /*
  const retrieveManufacturerList = async (audiences) => {
    let payload = { sort: ["account_status:asc"] };
    payload["q"] = "";

    let myFilter = { account_type: "ma", id: { eq: audiences } };

    const newFilterStr = filterParser(myFilter);
    console.log(newFilterStr);
    if (newFilterStr) {
      payload["filter"] = newFilterStr;
    }
    const { data } = await api.users.search(payload);
    console.log(data);
    setMaList(data ? data : []);
  };
*/

  const handleOpenChat = (room) => {
    setSelectedChatroomId(room.id);
    setChatroomKey(nanoid());
    setChatVisible(true);
  };

  if (pageLoading) {
    return (
      <div sx={{ p: 3 }}>
        <SuiBox display="flex" alignItems="center" gap={3}>
          <PulseLoader />
          <SuiTypography variant="h6" fontWeight="medium">
            Loading...
          </SuiTypography>
        </SuiBox>
      </div>
    );
  }

  const getProjectCurrentDate = (project) => {
    let prefix = "";
    let date = "";
    if (project.status == "active") {
      prefix = "Started at: ";
      date = project?.metadata?.actived_at
        ? moment(project.metadata.actived_at).format("MM/DD/yyyy")
        : "No Date";
    } else if (project.status == "completed") {
      prefix = "Finished at: ";
      date = project?.metadata?.completed_at
        ? moment(project.metadata.completed_at).format("MM/DD/yyyy")
        : "No Date";
    } else if (project.status == "canceled") {
      prefix = "Canceled at: ";
      date = project?.metadata?.canceled_at
        ? moment(project.metadata.canceled_at).format("MM/DD/yyyy")
        : "No Date";
    } else {
      prefix = "";
    }
    if (prefix) {
      return (
        <SuiTypography
          variant="body1"
          textTransform="none"
          color="secondary"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "1",
            WebkitBoxOrient: "vertical",
          }}
        >
          {prefix} {date}
        </SuiTypography>
      );
    } else {
      return null;
    }
  };

  const UpdateStatusDailog = () => {
    const [selectedProvider, setSelectedProvider] = useState({});
    var providers = chatrooms?.map((c) => {
      return { value: c.contactid, label: c.name };
    });

    useEffect(() => {
      //console.log('updateStatusDailogInfo', updateStatusDailogInfo, 'chatrooms', chatrooms, 'provider_id', project.provider_id)
      if (updateStatusDailogInfo.type !== "start" && chatrooms?.length > 0) {
        setSelectedProvider({ value: chatrooms[0].contactid, label: chatrooms[0].name });
      }
    }, [updateStatusDailogInfo.type, chatrooms]);

    const sendMsg = (tag, pstatus, room, desc) => {
      let messagebody = {
        tag: tag,
        text: tag + " Request",
        type: 3,
        desc: desc,
        pstatus: pstatus,
      };
      try {
        api.chatroom.sendmessage(room.id, messagebody).then(({ data }) => {
          handleOpenChat(room);
          return data;
        });
      } catch (err) {
        toast.error(`sendmessage failed!`);
        console.log(err);
      }
    };

    var title = "",
      msg = "",
      proj_name = project.name,
      ma_name = selectedProvider.label;
    if (updateStatusDailogInfo.type === "start") {
      title = "Select Provider";
      msg = "Please select one of the manufacturers who has sent inquiry to you.";
    } else if (updateStatusDailogInfo.type === "cancel") {
      title = "Cancel Project";
      msg = `Are you sure you will no longer be working on ${proj_name} with ${ma_name}?`;
    } else if (updateStatusDailogInfo.type === "complete") {
      title = "Complete Project";
      msg = `Are you sure you want to complete project ${proj_name} with ${ma_name}?`;
    }
    return (
      <Dialog
        hideBackdrop={true}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              width: "450px",
              height: "500px",
            },
          },
        }}
        open={updateStatusDailogInfo.show}
        onClose={() => setUpdateStatusDailogInfo({ show: false })}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {title}
          <IconButton
            aria-label="close"
            onClick={() => {
              setUpdateStatusDailogInfo({ show: false });
            }}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <SuiBox sx={{ mb: 2 }} display="flex" justifyContent="space-between" alignItems="center">
            <SuiTypography variant="body1">{msg}</SuiTypography>
          </SuiBox>
          {updateStatusDailogInfo.type === "start" && (
            <SuiSelect
              size="small"
              placeholder="Select provider to start project"
              value={selectedProvider}
              className="grow"
              options={providers}
              onChange={async (option) => {
                setSelectedProvider(option);
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <SuiButton
            disabled={!selectedProvider?.label}
            onClick={async () => {
              var room = chatrooms?.find((c) => c.contactid === selectedProvider.value);
              var de_name = store.name;
              if (updateStatusDailogInfo.type === "start")
                sendMsg(
                  "Start Project",
                  "active",
                  room,
                  `<b><i>${de_name}</i></b> would like to start working on <b><i>${proj_name}</i></b> with <b><i>${ma_name}</i></b>. To confirm that you're working together, please select ACCEPT.\nOnce you accept this request, Upstyled will notify the other party so that you two can begin collaborating.`
                );
              else if (updateStatusDailogInfo.type === "cancel")
                sendMsg(
                  "Cancel Project",
                  "canceled",
                  room,
                  `Unfortunately, <b><i>${de_name}</i></b> will no longer be working with <b><i>${ma_name}</i></b> on <b><i>${proj_name}</i></b>.\nPlease revisit the Project Dashboard and connect with our community of independent designers.`
                );
              else if (updateStatusDailogInfo.type === "complete")
                sendMsg(
                  "Complete Project",
                  "completed",
                  room,
                  `Congratulations, <b><i>${ma_name}</i></b>! <b><i>${de_name}</i></b> would like to confirm that project <b><i>${proj_name}</i></b> has been completed.\nTo confirm, please select ACCEPT. If project hasn't been completed, please select DECLINE and reach out to <b><i>${de_name}</i></b> to resolve any issues.`
                );
              toast.success(
                "We have send your request to the manufacturer. Please check the requirement and reply in the chat room with " +
                  selectedProvider?.label,
                { duration: 10000 }
              );
              setUpdateStatusDailogInfo({ show: false });
            }}
            color={"dark"}
            variant="gradient"
          >
            {title}
          </SuiButton>
        </DialogActions>
      </Dialog>
    );
  };

  //set project to public, so all manufacturers can view
  const handlePublishProject = async () => {
    const payload = {
      visibility: "public",
    };

    try {
      setUploading(true);
      await api.project.update(data.id, payload);
      const res = await api.project.updateStatus({ id: data.id, status: "requested" });
      setTimeout(() => {
        setUploading(false);

        toast.success("Your Project has been successfully posted to all Manufacturers!");
        setData({ ...res?.data?.updated, visibility: "public" });
      }, 1000);
    } catch (err) {
      setUploading(false);

      toast.error("Your Project could not be posted to all Manufacturers!");
      console.log(err);
    }
  };

  return data.id ? (
    <div>
      <Card sx={{ p: 3, my: 2 }}>
        <Backdrop sx={{ color: "info", zIndex: (theme) => 99999 }} open={uploading}>
          <CircularProgress color="info" sx={{ mr: 1 }} size={32} />
          <SuiTypography variant="h6" fontWeight="medium" color="info">
            Processing...
          </SuiTypography>
        </Backdrop>
        <SuiBox>
          <SuiBox
            display="flex"
            justifyContent="space-between"
            sx={{ flex: "flex-wrap" }}
            alignItems="flex-start"
            gap={2}
          >
            <SuiBox display="flex" justifyContent="flex-start" alignItems="center" py={0.5}>
              <SuiBox>
                <Tooltip title={data.name}>
                  <SuiBox>
                    <SuiTypography
                      variant="h5"
                      fontWeight="medium"
                      textTransform="capitalize"
                      lineHeight="1.2"
                      sx={{
                        width: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: "1",
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {data.name}
                    </SuiTypography>
                  </SuiBox>
                </Tooltip>
              </SuiBox>
              <SuiBox>
                {data?.status != "completed" && data?.status != "canceled" ? (
                  <SuiButton
                    className="small:px-4 medium:px-6 whitespace-nowrap"
                    component={Link}
                    to={`/workspace/edit-project?id=${data.id}`}
                    variant="text"
                    size="small"
                  >
                    <SuiTypography variant="text" textTransform="uppercase">
                      Edit Project
                    </SuiTypography>
                  </SuiButton>
                ) : null}
              </SuiBox>

              <SuiBox>
                <Tooltip
                  title={
                    data.status == "requested"
                      ? "Your project has been sent to manufacturers and is waiting for responses"
                      : ""
                  }
                >
                  <ProjectStatusTag status={data.status} />
                </Tooltip>
              </SuiBox>
              <SuiBox display="flex" alignItems="center" justifyContent="space-between" ml={2}>
                <SuiBox display="flex" gap={1} alignItems="center" justifyContent="center">
                  {data?.visibility == "public" ? (
                    <SuiBadge
                      color="success"
                      variant="contained"
                      size="xs"
                      badgeContent={
                        <SuiBox
                          sx={{ fontSize: 12, px: 0.5, py: 0.5 }}
                          fontWeight="bold"
                          color="inherit"
                        >
                          Published
                        </SuiBox>
                      }
                    />
                  ) : (
                    <Tooltip title="Add your project to our public job board to be viewed by ALL manufacturers (registered)">
                      <SuiButton
                        className="small:px-4 medium:px-6 whitespace-nowrap"
                        variant="gradient"
                        color="dark"
                        size="small"
                        onClick={() => handlePublishProject()}
                      >
                        <SuiBox
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          gap={0.5}
                          className=" w-max-24"
                          color="white"
                        >
                          <CalendarTodayIcon color="white" />
                          <SuiBox>
                            <SuiTypography
                              color="white"
                              variant="text"
                              textTransform="uppercase"
                              className="xsmall:hidden small:inline"
                            >
                              Publish Project
                            </SuiTypography>
                          </SuiBox>
                        </SuiBox>
                      </SuiButton>
                    </Tooltip>
                  )}
                  <SuiBox>
                    {(data?.status == "init" || data?.status == "requested") &&
                    ((data?.audiences && data.audiences.length < MAX_SELECTED_COUNT) ||
                      !data.audiences) ? (
                      <SuiButton
                        className="small:px-4 medium:px-6 whitespace-nowrap"
                        component={Link}
                        to={`/designers/publish-project?id=${data?.id}`}
                        variant="gradient"
                        color="dark"
                        size="small"
                      >
                        <SuiBox
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          gap={0.5}
                          className=" w-max-24"
                          color="white"
                        >
                          <CalendarTodayIcon color="white" />
                          <SuiBox>
                            <SuiTypography
                              color="white"
                              variant="text"
                              textTransform="uppercase"
                              className="xsmall:hidden small:inline"
                            >
                              {data?.audiences && data.audiences.length > 0
                                ? "Send More Requests"
                                : "Send Requests"}
                            </SuiTypography>
                          </SuiBox>
                        </SuiBox>
                      </SuiButton>
                    ) : null}
                  </SuiBox>
                  <UpdateStatusDailog />
                  {data?.status == "requested" && chatrooms?.length > 0 && (
                    <SuiBox>
                      <SuiButton
                        className="small:px-4 medium:px-6 whitespace-nowrap"
                        variant="gradient"
                        color="dark"
                        size="small"
                        onClick={(e) => {
                          setUpdateStatusDailogInfo({ show: true, type: "start" });
                        }}
                      >
                        Start Project
                      </SuiButton>
                    </SuiBox>
                  )}
                  {data?.status == "active" && (
                    <SuiBox>
                      <SuiButton
                        className="small:px-4 medium:px-6 whitespace-nowrap"
                        variant="gradient"
                        color="dark"
                        size="small"
                        onClick={(e) => {
                          setUpdateStatusDailogInfo({ show: true, type: "cancel" });
                        }}
                      >
                        Cancel Project
                      </SuiButton>
                    </SuiBox>
                  )}
                  {data?.status == "active" && (
                    <SuiBox>
                      <SuiButton
                        className="small:px-4 medium:px-6 whitespace-nowrap"
                        variant="gradient"
                        color="dark"
                        size="small"
                        onClick={(e) => {
                          setUpdateStatusDailogInfo({ show: true, type: "complete" });
                        }}
                      >
                        Complete Project
                      </SuiButton>
                    </SuiBox>
                  )}
                  <div />
                </SuiBox>
              </SuiBox>
            </SuiBox>
            {getProjectCurrentDate(data)}
          </SuiBox>
          {data.provider_id ? (
            <SuiBox
              key={"Service Provider"}
              display="flex"
              alignItems="center"
              py={1}
              pr={2}
              className="gap-1 items-start"
            >
              <SuiTypography
                variant="button"
                fontWeight="bold"
                textTransform="capitalize"
                sx={{ whiteSpace: "nowrap" }}
              >
                Service Provider:
              </SuiTypography>

              <SuiBox display="flex" justifyContent="flex-start" alignItems="center" gap={1}>
                <SuiBox component={Link} to={`/wall/portfolio/${data.provider_id}`}>
                  <Tooltip position="top" title={provider.name}>
                    <SuiAvatar
                      src={provider?.metadata?.avatar}
                      alt="profile-image"
                      variant="circle"
                      size="xs"
                      shadow="sm"
                    />
                  </Tooltip>
                </SuiBox>
                <SuiBox component={Link} to={`/wall/portfolio/${data.provider_id}`}>
                  <SuiTypography variant="button" fontWeight="regular" textTransform="capitalize">
                    {provider.name}
                  </SuiTypography>
                </SuiBox>
              </SuiBox>
              {/* <SuiBox
                sx={{ cursor: "pointer" }}
                onClick={() => setChatVisible(true)}
                display="flex"
                justifyContent="center"
                gap={0.5}
              >
                {" "}
                <Badge variant="dot" color="error" invisible={unReadCount ? false : true}>
                  <ChatIcon size={5} color="dark" />
                </Badge>
                <SuiBox>
                  <SuiTypography
                    sx={{ "&:hover": { textDecoration: "underline", color: "info" } }}
                    variant="button"
                    fontWeight="regular"
                    textTransform="capitalize"
                  >
                    Message
                  </SuiTypography>{" "}
                </SuiBox>
              </SuiBox> */}
            </SuiBox>
          ) : null}

          {/* <SuiBox display="flex" gap={1} alignItems="flex-start">
            <SuiBox style={{ fontWeight: "bold" }} color="dark" variant="body1" component="span">
              Instruction:
            </SuiBox>
            <SuiTypography size="small" variant="body1" textTransform="none" color="secondary">
              Once manufacturers send you inquiries, you will receive notifications and chat with
              them on this page. Once you decide which manufacturer you want to work with, click the
              “Manage Project” button in the chatroom to start the project.
            </SuiTypography>
          </SuiBox> */}
        </SuiBox>
        <SuiBox position="relative">
          <Grid container>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              {project?.products && project.products.length > 0 ? (
                <ProjectStyles project={project} />
              ) : (
                <ProjectInfo project={project} showResponseStatus={true} ref={projectInfoRef} />
              )}
            </Grid>
          </Grid>
          <SuiBox mt={2}>
            {project?.products && project.products.length > 0 ? (
              <ProjectInfo project={project} showResponseStatus={true} ref={projectInfoRef} />
            ) : null}
          </SuiBox>
          {chatrooms && chatrooms.length === 1 && (
            <SuiBox
              style={{
                position: "absolute",
                top: messageDialPosition.top,
                left: messageDialPosition.left,
              }}
            >
              <Draggable
                onDrag={() => setDragging(true)}
                onStop={() =>
                  setTimeout(() => {
                    setDragging(false);
                  }, 500)
                }
              >
                <div>
                  <SpeedDial
                    ref={dialButtonRef}
                    ariaLabel="open chat room"
                    sx={{
                      borderRadius: 0,
                      animation: shake && !chatVisible ? "shake 0.5s" : "",
                    }}
                    FabProps={{
                      variant: "extended",
                      color: "dark",
                      className: "text-gray-100 hover:text-gray-800",
                    }}
                    icon={
                      <SuiBox
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        color="inherit"
                      >
                        {" "}
                        <div className="flex items-center gap-1">
                          <Badge variant="dot" color="error" invisible={unReadCount ? false : true}>
                            <ChatIcon size={7} color="inherit" />
                          </Badge>
                          <div variant="h6" fontWeight="regular" textTransform="capitalize">
                            Message
                          </div>{" "}
                        </div>
                        <Divider type="vertical" sx={{ color: "inherit" }} />
                        <Avatar src={chatrooms[0].avatar} sx={{ width: 26, height: 26, mr: 0.5 }} />
                        {chatrooms[0].name}
                      </SuiBox>
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      dragging ? null : setChatVisible(!chatVisible);
                    }}
                    onClose={null}
                    onOpen={null}
                    open={false}
                  />{" "}
                </div>
              </Draggable>{" "}
            </SuiBox>
          )}
          {chatrooms && chatrooms.length > 1 && (
            <SuiBox
              style={{
                position: "absolute",
                top: messageDialPosition.top,
                left: messageDialPosition.left,
              }}
            >
              <Draggable
                onDrag={() => setDragging(true)}
                onStop={() =>
                  setTimeout(() => {
                    setDragging(false);
                  }, 500)
                }
              >
                <div>
                  <SuiBox>
                    <SpeedDial
                      ref={dialButtonRef}
                      ariaLabel="open chat room"
                      direction="up"
                      sx={{
                        borderRadius: 0,

                        animation: shake && !chatVisible ? "shake 0.5s" : "",
                      }}
                      FabProps={{
                        variant: "extended",
                        color: "dark",
                        className: "text-gray-100 hover:text-gray-800",
                      }}
                      icon={
                        <SuiBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          color="inherit"
                        >
                          {" "}
                          <div className="flex items-center gap-1">
                            <Badge
                              variant="dot"
                              color="error"
                              invisible={unReadCount ? false : true}
                            >
                              <ChatIcon size={7} color="inherit" />
                            </Badge>
                            <div variant="h6" fontWeight="regular" textTransform="capitalize">
                              Message
                            </div>{" "}
                          </div>
                          <Divider type="vertical" />
                          <AvatarGroup
                            max={3}
                            total={chatrooms.length}
                            spacing="medium"
                            slotProps={{
                              additionalAvatar: {
                                sx: { width: 26, height: 26, fontSize: "12px" },
                                className: "border",
                              },
                            }}
                            sx={{ color: "inherit" }}
                          >
                            {chatrooms.map((room) => (
                              <Avatar
                                src={room ? room?.avatar : contact?.avatar}
                                sx={{ width: 26, height: 26 }}
                                className="border"
                              />
                            ))}
                          </AvatarGroup>
                        </SuiBox>
                      }
                      onClose={() => setOpen(false)}
                      onOpen={() => setOpen(true)}
                      open={open}
                    >
                      {chatrooms.map((room) => (
                        <SpeedDialAction
                          key={room.name}
                          FabProps={{
                            sx: { width: "100%", height: "100%", py: 0.5, px: 1, mt: 1, mb: 0 },
                            variant: "extended",
                          }}
                          icon={
                            <SuiBox
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                width: "100%",
                                gap: 1,
                              }}
                            >
                              <Badge
                                variant="dot"
                                color="error"
                                invisible={room.ts > room.viewts ? false : true}
                                overlap="circular"
                              >
                                <Avatar
                                  src={room ? room?.avatar : contact?.avatar}
                                  sx={{ width: 32, height: 32 }}
                                />
                              </Badge>
                              <SuiTypography
                                variant="body1"
                                fontWeight="bold"
                                sx={{ whiteSpace: "nowrap" }}
                              >
                                {room.name}
                              </SuiTypography>
                            </SuiBox>
                          }
                          tooltipOpen={false}
                          onClick={() => handleOpenChat(room)}
                        ></SpeedDialAction>
                      ))}
                    </SpeedDial>
                  </SuiBox>
                </div>
              </Draggable>{" "}
            </SuiBox>
          )}
        </SuiBox>
        {privateVisible ? (
          <Prompt
            heading="Project Visibility"
            text={
              <SuiBox>
                <SuiBox>
                  Are you sure to change your project visibility to{" "}
                  {isPublic == true ? "Private" : "Public"}.{" "}
                </SuiBox>
                <SuiBox>
                  {isPublic == true &&
                  (!project?.audiences || (project?.audiences && project?.audiences.length == 0))
                    ? "Your project will be invisible to manufacturers before you select any manufacturer to send request."
                    : ""}
                </SuiBox>
              </SuiBox>
            }
            visible={privateVisible}
            okText="Update"
            okColor="dark"
            handleOk={() => {
              const visibilityTo = isPublic == true ? "private" : "public";
              const payload = {
                visibility: visibilityTo,
              };
              api.project
                .update(project.id, payload)
                .then(() => {
                  toast.success(
                    `Change project visibility to project ${visibilityTo} successfully!`
                  );
                  setIsPublic(!isPublic);
                  setPrivateVisible(false);
                })
                .catch((err) => {
                  console.log(err);
                  toast.error(`Change project visibility to project ${visibilityTo} failed!`);
                });
            }}
            handleClose={() => setPrivateVisible(false)}
          />
        ) : null}

        {chatVisible ? (
          <Card
            sx={{ maxWidth: "560px", maxHeight: "80%", overflow: "auto" }}
            className="fixed top-[10%] right-[5%] z-10"
          >
            <div>
              <SuiBox
                px={2}
                py={1}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <SuiBox>
                  <SuiTypography variant="h5">Chat with manufacturer</SuiTypography>
                </SuiBox>
                <IconButton
                  aria-label="close"
                  onClick={() => {
                    setChatVisible(false);
                    refreshChatRooms(project);
                  }}
                  sx={{
                    float: "right",
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </SuiBox>

              <SuiBox sx={{ overflow: "auto" }}>
                {chatrooms.length === 1 ? (
                  <ChatRoom
                    id={chatrooms[0].id}
                    regardingid={project.id}
                    cardClassName={`bg-transparent shadow-none`}
                  />
                ) : (
                  <SuiBox sx={{ display: "flex" }}>
                    <SuiBox mt={2} sx={{ overflowY: "auto", ml: 1, minWidth: "60px" }}>
                      {chatrooms.map((room) => (
                        <SuiBox sx={{ textAlign: "center", mb: 2 }}>
                          <SuiBox
                            sx={{
                              p: 1,
                              backgroundColor: ({ palette }) =>
                                selectedChatroomId == room.id ? palette.info.light : "transparent",
                              borderRadius: ({ borders: { borderRadius } }) => borderRadius.md,
                              cursor: "pointer",
                            }}
                            onClick={() => setSelectedChatroomId(room.id)}
                          >
                            <Tooltip title={room.name}>
                              <Badge
                                variant="dot"
                                color="error"
                                invisible={room.ts > room.viewts ? false : true}
                                overlap="circular"
                              >
                                <ContactAvatar
                                  contact={room ? room?.avatar : contact?.avatar}
                                  sx={{ width: 32, height: 32 }}
                                />
                              </Badge>
                            </Tooltip>
                          </SuiBox>
                        </SuiBox>
                      ))}
                    </SuiBox>
                    <Divider type="vertical" style={{ height: "60vh" }} />
                    <SuiBox>
                      <ChatRoom
                        key={chatroomKey}
                        id={selectedChatroomId}
                        regardingid={project.id}
                        cardClassName={`bg-transparent shadow-none`}
                      />
                    </SuiBox>
                  </SuiBox>
                )}
              </SuiBox>
            </div>
          </Card>
        ) : null}
      </Card>
    </div>
  ) : null;
};

export default ProjectDetail;
